import {
    FormControl,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import React, {FC} from "react";
import {array, date, object, string, TypeOf} from "zod";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useGetTimberTypePresentationQuery} from "../../../apis/TimberType.api.ts";
import {useGetTimberClassPresentationQuery} from "../../../apis/TimberClass.api.ts";
import {AsyncFormSelect, FormDateRange, FormSelect} from "../../../../share";
import {PrimaryCutting, toString as toStringCutting} from "../../../types/models/Cutting.ts";
import {toString as toStringTimberType} from "../../../types/models/TimberType.ts";
import {toString as toStringTimberClass} from "../../../types/models/TimberClass.ts";
import {toString as toStringTimberStorage} from "../../../types/models/TimberStorage.ts";
import {formatDateTimeParam} from "../../../../utils/formatDate.ts";
import Box from "@mui/material/Box";
import {useGetTimberStoragePresentationQuery} from "../../../apis/TimberStorage.api.ts";
import {WoodGroupsList} from "../../../types/models/MergeGroups.ts";
import * as crudgen from "../../../../share";
import {ShipmentTypePairs, toString as toStringShipmentType} from "../../../types/models/ShipmentType.d";
import {emptyListPaginationResult, listPaginationLimits} from "../../../../types/global.ts";
import {useGetCuttingPresentationQuery, useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import Toolbar from "@mui/material/Toolbar";
import useSearchDebounce from "../../../../hooks/useSearchDebounce.ts";
import {t} from "@lingui/core/macro";
import TimberStorageName, {
    TimberStorageNameDisplay
} from "../../timberStorage/TimberStorageDetail/TimberStorageName.tsx";
import TimberClassName, {TimberClassNameDisplay} from "../../timberClass/TimberClassDetail/TimberClassName.tsx";
import TimberTypeName, {TimberTypeNameDisplay} from "../../timberType/TimberTypeDetail/TimberTypeName.tsx";
import CuttingName, {CuttingNameDisplay} from "../../cutting/CuttingDetail/CuttingName.tsx";
import StorageShipmentPies from "./StorageShipmentPies.tsx";
import {GenericCordwoodsShipments, GenericLogsShipments} from "./GenericRootShipments.tsx";
import TabPanel from "../../../../share/components/tab/TabPanel.tsx";
import GenericRootTable from "./GenericRootTable.tsx";
import useSearch from "../../../../hooks/useSearch.ts";

const initialFormState = {
    shipmentType: '',
    typeId: [],
    classId: [],
    cuttingId: [],
    storageId: [],
    startCreate: null,
    endCreate: null,
    startShipment: null,
    endShipment: null,
}

const formSchema = object({
    shipmentType: string().nullish(),
    typeId: array(string()).nullish(),
    classId: array(string()).nullish(),
    cuttingId: array(string()).nullish(),
    storageId: array(string()).nullish(),
    startCreate: date().nullish(),
    endCreate: date().nullish(),
    startShipment: date().nullish(),
    endShipment: date().nullish(),
});

export type FormInput = TypeOf<typeof formSchema>;


type FormOptions = {
    showShipmentType: boolean;
    showCutting: boolean;
    showStorage: boolean;
    showCreateDate: boolean;
    showShipmentDate: boolean;
}

export const defaultFormOptions: FormOptions = {
    showShipmentType: true,
    showCutting: true,
    showStorage: true,
    showCreateDate: true,
    showShipmentDate: true,
}

type IGenericRootReportProps = {
    mainTitle: string;
    shipmentDefined: boolean | null;
    formOptions: FormOptions;
    customizeFiler: (filter: Record<string, any | undefined>) => Record<string, any | undefined>;
    toolbar?: (groupedData: WoodGroupsList) => JSX.Element | undefined;
    initialState: FormInput;
    showShipments?: boolean;
};

const GenericRootReport: FC<IGenericRootReportProps> = ({
                                                            mainTitle,
                                                            shipmentDefined,
                                                            formOptions = defaultFormOptions,
                                                            customizeFiler,
                                                            toolbar = undefined,
                                                            initialState = initialFormState,
                                                            showShipments = true,
                                                        }) => {

    const searchParams = new URLSearchParams(window.location.search)
    const [tabValue, setTabValue] = React.useState(searchParams.get('reportTab') || 'data');
    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {

        if (searchParams.has('reportTab')) {
            searchParams.set('reportTab', newValue)
        } else {
            searchParams.append('reportTab', newValue)
        }
        window.history.pushState({}, '', '?' + searchParams.toString())

        setTabValue(newValue);
    };

    const {search, updateSearch} = useSearch(`Report${mainTitle}`)

    const methods = useForm<FormInput>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...initialFormState,
            ...initialState,
            ...search,
        }
    });

    const formData = useWatch({
        control: methods.control,
        name: ['classId', 'typeId', 'cuttingId', 'storageId', 'shipmentType', 'startCreate', 'endCreate', 'startShipment', 'endShipment']
    })

    const filter = React.useMemo(() => {
        const [classId, typeId, cuttingId, storageId, shipmentType, startCreate, endCreate, startShipment, endShipment] = formData
        return customizeFiler({
            classId,
            typeId,
            cuttingId,
            storageId,
            shipmentType,
            startCreate: formOptions.showCreateDate ? formatDateTimeParam(startCreate) : undefined,
            endCreate: formOptions.showCreateDate ? formatDateTimeParam(endCreate) : undefined,
            startShipment: (shipmentDefined && formOptions.showShipmentDate) ? formatDateTimeParam(startShipment) : undefined,
            endShipment: (shipmentDefined && formOptions.showShipmentDate) ? formatDateTimeParam(endShipment) : undefined,
            shipmentDefined,
        })
    }, [customizeFiler, formData, shipmentDefined])

    React.useEffect(() => {
        const [classId, typeId, cuttingId, storageId, shipmentType, startCreate, endCreate, startShipment, endShipment] = formData
        updateSearch(true, {
            classId, typeId, cuttingId, storageId, shipmentType, startCreate, endCreate, startShipment, endShipment
        })
    }, [formData])

    const [, setCuttingInputValue, cuttingInputTerm] = useSearchDebounce();
    const {
        data: cuttingList = emptyListPaginationResult,
        isLoading: getCuttingLoading,
        isFetching: getCuttingFetching,
    } = useGetCuttingPresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: cuttingInputTerm,
        },
    }, {skip: !formOptions.showCutting})

    const [, setStorageInputValue, storageInputTerm] = useSearchDebounce();
    const {
        data: storageList = emptyListPaginationResult,
        isLoading: getStorageLoading,
        isFetching: getStorageFetching,
    } = useGetTimberStoragePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: storageInputTerm,
            cuttingIdRefs: filter.cuttingId,
        },
    }, {skip: !formOptions.showStorage})

    const [, setTimberTypeInputValue, timberTypeInputTerm] = useSearchDebounce();
    const {
        data: timberTypeList = emptyListPaginationResult,
        isLoading: getTimberTypeListLoading,
        isFetching: getTimberTypeListFetching,
    } = useGetTimberTypePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: timberTypeInputTerm,
        },
    })

    const [, setTimberClassInputValue, timberClassInputTerm] = useSearchDebounce();
    const {
        data: timberClassList = emptyListPaginationResult,
        isLoading: getTimberClassListLoading,
        isFetching: getTimberClassListFetching,
    } = useGetTimberClassPresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: timberClassInputTerm,
        },
    })

    const {data: selectedCutting} = useGetCuttingQuery(PrimaryCutting.fromId(filter.cuttingId),
        {
            skip: !(filter.cuttingId) || Array.isArray(filter.cuttingId)
        }
    )

    const titleSuffix = (shipmentDefined ? "shipt" : shipmentDefined === false ? "not-shipt" : "all")
    const title = `${mainTitle} ${titleSuffix}`

    return (
        <Box>
            <Paper>
                <Toolbar
                    sx={{
                        pl: {sm: 2},
                        pr: {xs: 1, sm: 1},
                    }}
                >
                    <Typography
                        sx={{flex: '1 1 100%'}}
                        variant="h6"
                        component="div"
                    >
                        {title}
                    </Typography>
                </Toolbar>
                <Box>
                    <Paper sx={{padding: "20px"}}>
                        <Stack direction="column" spacing={3}>
                            <FormProvider {...methods}>
                                <FormControl size="small">
                                    {formOptions.showShipmentType &&
                                        <FormSelect
                                            key='shipmentType'
                                            name='shipmentType'
                                            label={t`Shipment Type`}
                                            options={ShipmentTypePairs}
                                            optionLabelMap={toStringShipmentType}
                                            optionsLoading={false}
                                        />
                                    }
                                    {formOptions.showCutting &&
                                        <AsyncFormSelect
                                            key='cuttingId'
                                            name='cuttingId'
                                            label={t`Cutting`}
                                            multiple
                                            options={cuttingList.items}
                                            optionsLoading={getCuttingLoading || getCuttingFetching}
                                            optionLabelMap={toStringCutting}
                                            onInputChange={setCuttingInputValue}
                                            renderOption={(optionProps, option, {inputValue}) => {
                                                const {key, ...filteredOptionProps} = optionProps;
                                                return (
                                                    <li key={key} {...filteredOptionProps}><CuttingNameDisplay data={option}/></li>
                                                )
                                            }}
                                            renderValue={(value, getItemProps) =>
                                                value.map((option: any, index: number) => {
                                                    const {key, ...tagProps} = getItemProps({index})
                                                    return (
                                                        <crudgen.SelectChip key={key} {...tagProps}>
                                                            <CuttingName id={option}/>
                                                        </crudgen.SelectChip>
                                                    );
                                                })
                                            }
                                        />
                                    }
                                    {formOptions.showStorage &&
                                        <AsyncFormSelect
                                            key='storageId'
                                            name='storageId'
                                            label={t`Storage`}
                                            multiple
                                            options={storageList.items}
                                            optionsLoading={getStorageLoading || getStorageFetching}
                                            optionLabelMap={toStringTimberStorage}
                                            onInputChange={setStorageInputValue}
                                            renderOption={(optionProps, option, {inputValue}) => {
                                                const {key, ...filteredOptionProps} = optionProps;
                                                return (
                                                    <li key={key} {...filteredOptionProps}><TimberStorageNameDisplay data={option}/></li>
                                                )
                                            }}
                                            renderValue={(value, getItemProps) =>
                                                value.map((option: any, index: number) => {
                                                    const {key, ...tagProps} = getItemProps({index})
                                                    return (
                                                        <crudgen.SelectChip key={key} {...tagProps}>
                                                            <TimberStorageName id={option}/>
                                                        </crudgen.SelectChip>
                                                    );
                                                })
                                            }
                                        />
                                    }
                                    <Stack direction="row" spacing={1}>
                                        <AsyncFormSelect
                                            key='typeId'
                                            name='typeId'
                                            label={t`Timber Type`}
                                            multiple
                                            options={timberTypeList.items}
                                            optionsLoading={getTimberTypeListLoading || getTimberTypeListFetching}
                                            optionLabelMap={toStringTimberType}
                                            onInputChange={setTimberTypeInputValue}
                                            renderOption={(optionProps, option, {inputValue}) => {
                                                const {key, ...filteredOptionProps} = optionProps;
                                                return (
                                                    <li key={key} {...filteredOptionProps}><TimberTypeNameDisplay data={option}/></li>)
                                            }}
                                            renderValue={(value, getItemProps) =>
                                                value.map((option: any, index: number) => {
                                                    const {key, ...tagProps} = getItemProps({index})
                                                    return (
                                                        <crudgen.SelectChip key={key} {...tagProps}>
                                                            <TimberTypeName id={option}/>
                                                        </crudgen.SelectChip>
                                                    );
                                                })
                                            }
                                        />
                                        <AsyncFormSelect
                                            key='classId'
                                            name='classId'
                                            label={t`Timber Class`}
                                            multiple
                                            options={timberClassList.items}
                                            optionsLoading={getTimberClassListLoading || getTimberClassListFetching}
                                            optionLabelMap={toStringTimberClass}
                                            onInputChange={setTimberClassInputValue}
                                            renderOption={(optionProps, option, {inputValue}) => {
                                                const {key, ...filteredOptionProps} = optionProps;
                                                return (
                                                    <li key={key} {...filteredOptionProps}><TimberClassNameDisplay data={option}/></li>)
                                            }}
                                            renderValue={(value, getItemProps) =>
                                                value.map((option: any, index: number) => {
                                                    const {key, ...tagProps} = getItemProps({index})
                                                    return (
                                                        <crudgen.SelectChip key={key} {...tagProps}>
                                                            <TimberClassName id={option}/>
                                                        </crudgen.SelectChip>
                                                    );
                                                })
                                            }
                                        />
                                    </Stack>
                                    {formOptions.showCreateDate &&
                                        <FormDateRange
                                            displayDateRange={ {
                                                startDate: selectedCutting?.startAt && new Date(selectedCutting?.startAt),
                                                endDate: !(selectedCutting?.endAt) || new Date(selectedCutting?.endAt) > new Date() ? new Date() : new Date(selectedCutting?.endAt)
                                            } }
                                            minDate={selectedCutting?.startAt}
                                            maxDate={selectedCutting?.endAt}
                                            startName="startCreate"
                                            endName="endCreate"
                                            startLabel={t`From`}
                                            endLabel={t`To Timber Input Date`}
                                        />}
                                    {shipmentDefined && formOptions.showShipmentDate &&
                                        <FormDateRange
                                            displayDateRange={ {
                                                startDate: selectedCutting?.startAt && new Date(selectedCutting?.startAt),
                                                endDate: !(selectedCutting?.endAt) || new Date(selectedCutting?.endAt) > new Date() ? new Date() : new Date(selectedCutting?.endAt)
                                            } }
                                            minDate={selectedCutting?.startAt}
                                            maxDate={selectedCutting?.endAt}
                                            startName="startShipment"
                                            endName="endShipment"
                                            startLabel={t`From`}
                                            endLabel={t`To Shipment Date`}
                                        />}
                                </FormControl>
                            </FormProvider>
                        </Stack>
                    </Paper>
                    <Box sx={ { width: '100%' } }>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            data-testid="Report-tabs"
                        >
                            <Tab label={t`Data`} value="data"/>
                            <Tab label={t`Graphs`} value="graphs"/>
                            { showShipments && shipmentDefined !== false && (!filter.shipmentType || filter.shipmentType == 'Logs') && <Tab label={t`Log Shipments`} value="logShipments"/> }
                            { showShipments && shipmentDefined !== false && (!filter.shipmentType || filter.shipmentType == 'Cordwood') && <Tab label={t`Cordwood Shipments`} value="cordwoodShipments"/> }
                        </Tabs>
                        <TabPanel value={tabValue} index="data" data-testid="data-tab">
                            <GenericRootTable
                                title={title}
                                toolbar={toolbar}
                                filter={filter}/>
                        </TabPanel>
                        <TabPanel value={tabValue} index="graphs" data-testid="graphs-tab">
                            <StorageShipmentPies filter={filter} showOnlyShipped={!!shipmentDefined}/>
                        </TabPanel>
                        {showShipments && shipmentDefined !== false && (!filter.shipmentType || filter.shipmentType == 'Logs') &&
                            <TabPanel value={tabValue} index="logShipments" data-testid="logShipments-tab">
                                <GenericLogsShipments
                                    filter={filter}/>
                            </TabPanel>
                        }
                        {showShipments && shipmentDefined !== false && (!filter.shipmentType || filter.shipmentType == 'Cordwood') &&
                            <TabPanel value={tabValue} index="cordwoodShipments" data-testid="cordwoodShipments-tab">
                                <GenericCordwoodsShipments
                                    filter={filter}/>
                            </TabPanel>
                        }
                    </Box>
                </Box>
            </Paper>
        </Box>
    )
}

export default GenericRootReport
